import React from 'react';

export const SHADOW_OPTIONS = [
    { id: 'none', label: 'None' },
    { id: 'sm', label: 'Small' },
    { id: 'md', label: 'Medium' },
    { id: 'lg', label: 'Large' },
];

export const SHADOW_CSS = {
    none: 'none',
    sm: '0 1px 2px rgba(0, 0, 0, 0.04)',
    md: '0 4px 12px rgba(0, 0, 0, 0.08)',
    lg: '0 10px 26px rgba(0, 0, 0, 0.16)',
};

export const BORDER_STYLE_OPTIONS = [
    { id: 'none', label: 'None' },
    { id: 'solid', label: 'Solid' },
    { id: 'dashed', label: 'Dashed' },
];

export const FONT_WEIGHT_OPTIONS = [
    { id: '400', label: 'Normal' },
    { id: '600', label: 'Semibold' },
    { id: '700', label: 'Bold' },
    { id: '800', label: 'Extrabold' },
];

export const ICON_SUGGESTIONS = [
    'fa-bolt', 'fa-chart-line', 'fa-temperature-half', 'fa-droplet',
    'fa-leaf', 'fa-fire', 'fa-battery-full', 'fa-plug',
    'fa-wind', 'fa-sun', 'fa-gauge-high', 'fa-bell',
];

export const POSITION_OPTIONS = [
    { id: 'top-left', label: 'Top left' },
    { id: 'top-center', label: 'Top center' },
    { id: 'top-right', label: 'Top right' },
    { id: 'middle-left', label: 'Middle left' },
    { id: 'center', label: 'Center' },
    { id: 'middle-right', label: 'Middle right' },
    { id: 'bottom-left', label: 'Bottom left' },
    { id: 'bottom-center', label: 'Bottom center' },
    { id: 'bottom-right', label: 'Bottom right' },
];

export const CUSTOM_STYLE_TILE = { id: 'custom', label: 'Custom', icon: 'fas fa-sliders-h' };
export const CUSTOMIZABLE_CATEGORIES = new Set(['gauge', 'metric', 'table']);

const GRADIENT_RE = /^linear-gradient\(135deg,\s*(#[0-9a-fA-F]{3,8}),\s*(#[0-9a-fA-F]{3,8})\)$/;

export function parseBackground(bg) {
    if (typeof bg === 'string') {
        const m = bg.match(GRADIENT_RE);
        if (m) return { type: 'gradient', start: m[1], end: m[2] };
    }
    return { type: 'solid', start: (typeof bg === 'string' && bg) || '#ffffff', end: '#8e6cff' };
}

export function buildBackgroundCss(type, start, end) {
    return type === 'gradient' ? `linear-gradient(135deg, ${start}, ${end})` : start;
}

export function computeCardStyle(settings = {}) {
    const accentColor = settings.color;
    const hasCustomBorder = settings.borderStyle && settings.borderStyle !== 'none';
    return {
        boxShadow: SHADOW_CSS[settings.shadow] || SHADOW_CSS.sm,
        ...(settings.background ? { background: settings.background } : null),
        ...(settings.borderRadius !== undefined && settings.borderRadius !== null
            ? { borderRadius: settings.borderRadius }
            : null),
        ...(hasCustomBorder
            ? { border: `${settings.borderWidth || 1}px ${settings.borderStyle} ${accentColor || '#e5e7eb'}` }
            : null),
        ...(settings.titleBg ? { '--widget-title-bg': settings.titleBg } : null),
        ...(settings.titleColor ? { '--widget-title-color': settings.titleColor } : null),
        ...(settings.titleFontSize ? { '--widget-title-font-size': `${settings.titleFontSize}px` } : null),
    };
}

export function computeBodyStyle(settings = {}) {
    return {
        ...(settings.fontSize ? { '--widget-font-size': `${settings.fontSize}px` } : null),
        ...(settings.fontWeight ? { '--widget-font-weight': settings.fontWeight } : null),
        ...(settings.textColor ? { '--widget-text-color': settings.textColor } : null),
        ...(settings.contentWidth ? { '--widget-content-width': `${settings.contentWidth}px` } : null),
        ...(settings.contentHeight ? { '--widget-content-height': `${settings.contentHeight}px` } : null),
    };
}

export const AppearanceFields = ({ settings, onChange }) => {
    const color = settings.color || '#4318FF';
    const { type: backgroundType, start: backgroundStart, end: backgroundEnd } = parseBackground(settings.background);
    const shadow = settings.shadow || 'sm';
    const borderStyle = settings.borderStyle || 'none';
    const borderWidth = settings.borderWidth ?? 2;
    const borderRadius = settings.borderRadius ?? 10;
    const fontSize = settings.fontSize ?? 28;
    const fontWeight = settings.fontWeight || '700';
    const textColor = settings.textColor || '#111827';
    const contentWidth = settings.contentWidth || '';
    const contentHeight = settings.contentHeight || '';

    const setBackground = (patch) => {
        const next = { type: backgroundType, start: backgroundStart, end: backgroundEnd, ...patch };
        onChange({ background: buildBackgroundCss(next.type, next.start, next.end) });
    };

    return (
        <div className="widget-settings-drawer__field">
            <span>Appearance</span>
            <div className="widget-style-fields__appearance">
                <label className="widget-style-fields__appearance-field">
                    <span>Accent color</span>
                    <input type="color" value={color} onChange={(e) => onChange({ color: e.target.value })} />
                </label>
                <label className="widget-style-fields__appearance-field">
                    <span>Background type</span>
                    <select value={backgroundType} onChange={(e) => setBackground({ type: e.target.value })}>
                        <option value="solid">Solid</option>
                        <option value="gradient">Gradient</option>
                    </select>
                </label>
                <label className="widget-style-fields__appearance-field">
                    <span>{backgroundType === 'gradient' ? 'Gradient start' : 'Background'}</span>
                    <input type="color" value={backgroundStart} onChange={(e) => setBackground({ start: e.target.value })} />
                </label>
                {backgroundType === 'gradient' && (
                    <label className="widget-style-fields__appearance-field">
                        <span>Gradient end</span>
                        <input type="color" value={backgroundEnd} onChange={(e) => setBackground({ end: e.target.value })} />
                    </label>
                )}
                <label className="widget-style-fields__appearance-field">
                    <span>Shadow</span>
                    <select value={shadow} onChange={(e) => onChange({ shadow: e.target.value })}>
                        {SHADOW_OPTIONS.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
                    </select>
                </label>
                <label className="widget-style-fields__appearance-field">
                    <span>Border</span>
                    <select value={borderStyle} onChange={(e) => onChange({ borderStyle: e.target.value })}>
                        {BORDER_STYLE_OPTIONS.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
                    </select>
                </label>
                {borderStyle !== 'none' && (
                    <label className="widget-style-fields__appearance-field">
                        <span>Border width</span>
                        <input type="number" min={1} max={8} value={borderWidth} onChange={(e) => onChange({ borderWidth: e.target.value })} />
                    </label>
                )}
                <label className="widget-style-fields__appearance-field">
                    <span>Corner radius</span>
                    <input type="number" min={0} max={40} value={borderRadius} onChange={(e) => onChange({ borderRadius: e.target.value })} />
                </label>
                <label className="widget-style-fields__appearance-field">
                    <span>Value font size</span>
                    <input type="number" min={10} max={64} value={fontSize} onChange={(e) => onChange({ fontSize: e.target.value })} />
                </label>
                <label className="widget-style-fields__appearance-field">
                    <span>Value font weight</span>
                    <select value={fontWeight} onChange={(e) => onChange({ fontWeight: e.target.value })}>
                        {FONT_WEIGHT_OPTIONS.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
                    </select>
                </label>
                <label className="widget-style-fields__appearance-field">
                    <span>Value text color</span>
                    <input type="color" value={textColor} onChange={(e) => onChange({ textColor: e.target.value })} />
                </label>
                <label className="widget-style-fields__appearance-field">
                    <span>Content width (px)</span>
                    <input type="number" min={0} max={2000} placeholder="Auto" value={contentWidth} onChange={(e) => onChange({ contentWidth: e.target.value })} />
                </label>
                <label className="widget-style-fields__appearance-field">
                    <span>Content height (px)</span>
                    <input type="number" min={0} max={2000} placeholder="Auto" value={contentHeight} onChange={(e) => onChange({ contentHeight: e.target.value })} />
                </label>
            </div>
            <div className="widget-style-fields__hint">
                Value font size/weight/color apply to every widget's numbers and labels (charts apply the color to their axis, legend, and data-label text). Accent color stays separate — it drives fills, needles, and series/line colors. Content width/height resize the gauge, chart, or 3D graphic itself within the card — leave blank to fill the card like before.
            </div>
        </div>
    );
};

export const TitleFields = ({ settings, onChange }) => {
    const titleColor = settings.titleColor || '#111827';
    const titleBg = settings.titleBg || '#f9fafb';
    const titleFontSize = settings.titleFontSize ?? 13;

    return (
        <div className="widget-settings-drawer__field">
            <span>Title bar</span>
            <div className="widget-style-fields__appearance">
                <label className="widget-style-fields__appearance-field">
                    <span>Title text color</span>
                    <input type="color" value={titleColor} onChange={(e) => onChange({ titleColor: e.target.value })} />
                </label>
                <label className="widget-style-fields__appearance-field">
                    <span>Title background</span>
                    <input type="color" value={titleBg} onChange={(e) => onChange({ titleBg: e.target.value })} />
                </label>
                <label className="widget-style-fields__appearance-field">
                    <span>Title font size</span>
                    <input type="number" min={10} max={24} value={titleFontSize} onChange={(e) => onChange({ titleFontSize: e.target.value })} />
                </label>
            </div>
        </div>
    );
};

export const GaugeShapeFields = ({ settings, onChange }) => {
    const trackShape = settings.customTrackShape || 'circle';
    const fillStyle = settings.customFillStyle || 'solid';
    const valueDisplay = settings.customValueDisplay || 'number';
    const orientation = settings.customOrientation || 'horizontal';
    const thickness = settings.customThickness ?? 14;

    return (
        <div className="widget-settings-drawer__field">
            <span>Custom shape</span>
            <div className="widget-style-fields__appearance">
                <label className="widget-style-fields__appearance-field">
                    <span>Track shape</span>
                    <select value={trackShape} onChange={(e) => onChange({ customTrackShape: e.target.value })}>
                        <option value="circle">Circle</option>
                        <option value="arc">Arc (half circle)</option>
                        <option value="bar">Bar</option>
                    </select>
                </label>
                {trackShape === 'bar' && (
                    <label className="widget-style-fields__appearance-field">
                        <span>Orientation</span>
                        <select value={orientation} onChange={(e) => onChange({ customOrientation: e.target.value })}>
                            <option value="horizontal">Horizontal</option>
                            <option value="vertical">Vertical</option>
                        </select>
                    </label>
                )}
                <label className="widget-style-fields__appearance-field">
                    <span>Fill style</span>
                    <select value={fillStyle} onChange={(e) => onChange({ customFillStyle: e.target.value })}>
                        <option value="solid">Solid</option>
                        <option value="segmented">Segmented</option>
                        {trackShape === 'arc' && <option value="needle">Needle</option>}
                    </select>
                </label>
                <label className="widget-style-fields__appearance-field">
                    <span>Value display</span>
                    <select value={valueDisplay} onChange={(e) => onChange({ customValueDisplay: e.target.value })}>
                        <option value="number">Number</option>
                        <option value="digital">Digital</option>
                        <option value="badge">Badge</option>
                    </select>
                </label>
                <label className="widget-style-fields__appearance-field">
                    <span>Track thickness</span>
                    <input type="number" min={4} max={40} value={thickness} onChange={(e) => onChange({ customThickness: e.target.value })} />
                </label>
            </div>
        </div>
    );
};

export const TableShapeFields = ({ settings, onChange }) => (
    <div className="widget-settings-drawer__field">
        <span>Custom style</span>
        <div className="widget-style-fields__appearance">
            {[
                ['customStriped', 'Striped rows'],
                ['customBordered', 'Bordered grid'],
                ['customCompact', 'Compact rows'],
                ['customDarkHeader', 'Dark header'],
                ['customHover', 'Hover highlight'],
            ].map(([key, label]) => (
                <label className="widget-style-fields__appearance-field widget-style-fields__appearance-field--checkbox" key={key}>
                    <input type="checkbox" checked={Boolean(settings[key])} onChange={(e) => onChange({ [key]: e.target.checked })} />
                    <span>{label}</span>
                </label>
            ))}
        </div>
    </div>
);

export const MetricShapeFields = ({ settings, onChange }) => {
    const showIcon = Boolean(settings.customShowIcon);
    const iconClass = settings.customIconClass || '';

    return (
        <div className="widget-settings-drawer__field">
            <span>Custom shape</span>
            <div className="widget-style-fields__hint">
                The card's shape itself (border, corners, background, gradient) comes from the Appearance section below — it applies to every widget, not just Metric.
            </div>
            <div className="widget-style-fields__appearance">
                <label className="widget-style-fields__appearance-field widget-style-fields__appearance-field--checkbox">
                    <input type="checkbox" checked={showIcon} onChange={(e) => onChange({ customShowIcon: e.target.checked })} />
                    <span>Show icon</span>
                </label>
                <label className="widget-style-fields__appearance-field widget-style-fields__appearance-field--checkbox">
                    <input
                        type="checkbox"
                        checked={Boolean(settings.customShowTrend)}
                        onChange={(e) => onChange({ customShowTrend: e.target.checked })}
                    />
                    <span>Show trend arrow</span>
                </label>
            </div>
            {showIcon && (
                <>
                    <label className="widget-style-fields__appearance-field">
                        <span>Icon (type any FontAwesome class, or pick one)</span>
                        <input
                            type="text"
                            value={iconClass}
                            onChange={(e) => onChange({ customIconClass: e.target.value })}
                            placeholder="fas fa-bolt"
                        />
                    </label>
                    <div className="widget-style-fields__icon-picker">
                        {ICON_SUGGESTIONS.map((cls) => (
                            <button
                                type="button"
                                key={cls}
                                className={`widget-style-fields__icon-swatch${iconClass === `fas ${cls}` ? ' widget-style-fields__icon-swatch--active' : ''}`}
                                title={cls}
                                onClick={() => onChange({ customIconClass: `fas ${cls}` })}
                            >
                                <i className={`fas ${cls}`}></i>
                            </button>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export const MetricFieldsEditor = ({ fields, onChange }) => {
    const list = fields && fields.length ? fields : [{ title: '', unit: '', position: 'center' }];
    const update = (i, patch) => onChange(list.map((f, idx) => (idx === i ? { ...f, ...patch } : f)));
    const add = () => onChange([...list, { title: '', unit: '', position: 'center' }]);
    const remove = (i) => onChange(list.length > 1 ? list.filter((_, idx) => idx !== i) : list);

    return (
        <div className="widget-settings-drawer__field">
            <span>Fields</span>
            <div className="widget-style-fields__hint">
                Add as many fields as you need and choose where each sits on the card. Each field's value is
                read live from the API by matching its title to a meter reading (e.g. "Voltage R", "Current Y") —
                there's no manual value to type in.
            </div>
            {list.map((field, i) => (
                <div className="widget-style-fields__field-row" key={i}>
                    <input type="text" placeholder="Title (matches a meter reading)" value={field.title} onChange={(e) => update(i, { title: e.target.value })} />
                    <input type="text" placeholder="Unit" value={field.unit} onChange={(e) => update(i, { unit: e.target.value })} />
                    <select value={field.position || 'center'} onChange={(e) => update(i, { position: e.target.value })}>
                        {POSITION_OPTIONS.map((opt) => <option key={opt.id} value={opt.id}>{opt.label}</option>)}
                    </select>
                    <button type="button" className="widget-style-fields__row-remove" onClick={() => remove(i)} title="Remove field">
                        <i className="fas fa-trash"></i>
                    </button>
                </div>
            ))}
            <button type="button" className="widget-style-fields__add-row" onClick={add}>
                <i className="fas fa-plus"></i> Add field
            </button>
        </div>
    );
};
